import type { Dispatch } from "react";
import type { UnknownAction } from "redux";
import { useDispatch } from "react-redux";
import { Textbox } from "fabric/fabric-impl";
import { isText } from "../helpers/textHelpers";
import { TextAlignOption } from "../types/textTypes";
import {
  getProcessedCoordinatesAndDimensionsFromFabricObject,
  getProcessedFontSizeFromFabricObject,
  isObjectPartOfGroup,
} from "../utils/canvasUtils";
import { isImage } from "../utils/imageUtils";
import { isViewport } from "../utils/shapeUtils";
import { Object } from "../types/canvasTypes";
import { resetCurrentCanvasState, setImageOptions, setTextOptions } from "../../../store/canvas/canvasSlice";
import { resetCurrentInspectorState, setCurrentInspectorState } from "@/store/templateEditor/templateEditorSlice";

type CoordinatesAndDimensions = ReturnType<typeof getProcessedCoordinatesAndDimensionsFromFabricObject>;

export function useObjectSelectionChangeHandlers() {
  const dispatch = useDispatch();

  const handleObjectSelection = (selectedObjects: Object[]) => {
    if (isObjectSelectionEmpty(selectedObjects)) return;

    if (isGroupSelected(selectedObjects)) {
      processGroupSelection(dispatch);

      return;
    }

    const [object] = selectedObjects;
    const coordinatesAndDimensions = getProcessedCoordinatesAndDimensionsFromFabricObject(object);

    if (isViewport(object)) {
      processViewportObjectSelection(dispatch);
    } else if (isText(object)) {
      processTextObjectSelection({
        dispatch,
        object,
        coordinatesAndDimensions,
      });
    } else if (isImage(object)) {
      processImageObjectSelection({
        dispatch,
        object,
        coordinatesAndDimensions,
      });
    }
  };

  const handleObjectDeselection = () => {
    dispatch(resetCurrentCanvasState());
  };

  return {
    handleObjectSelection,
    handleObjectDeselection,
  };
}

function processViewportObjectSelection(dispatch: Dispatch<UnknownAction>) {
  console.log("processViewportObjectSelection");
  dispatch(resetCurrentInspectorState());
}

function processGroupSelection(dispatch: Dispatch<UnknownAction>) {
  dispatch(setCurrentInspectorState("group"));
}

function processTextObjectSelection({
  dispatch,
  object,
  coordinatesAndDimensions,
}: {
  dispatch: Dispatch<UnknownAction>;
  object: Textbox;
  coordinatesAndDimensions: CoordinatesAndDimensions;
}) {
  dispatch(setCurrentInspectorState("text"));
  dispatch(
    setTextOptions({
      strokeWidth: object.strokeWidth!,
      opacity: object.opacity!,
      textAlign: object.textAlign as TextAlignOption,
      stroke: object.stroke!,
      fill: object.fill as string,
      fontStyle: object.fontStyle!,
      angle: object.angle!,
      fontSize: getProcessedFontSizeFromFabricObject(object),
      fontFamily: object.fontFamily!,
      fontWeight: object.fontWeight!,
      underline: object.underline!,
      linethrough: object.linethrough!,
      width: coordinatesAndDimensions.width,
      top: coordinatesAndDimensions.top,
      left: coordinatesAndDimensions.left,
    })
  );
}

function processImageObjectSelection({
  dispatch,
  object,
  coordinatesAndDimensions,
}: {
  dispatch: Dispatch<UnknownAction>;
  object: Object;
  coordinatesAndDimensions: CoordinatesAndDimensions;
}) {
  dispatch(setCurrentInspectorState("image"));
  dispatch(
    setImageOptions({
      strokeWidth: object.strokeWidth!,
      opacity: object.opacity!,
      stroke: object.stroke!,
      angle: object.angle!,
      ...coordinatesAndDimensions,
    })
  );
}

function isObjectSelectionEmpty(selectedObjects: Object[]) {
  return selectedObjects.length === 0;
}

function isGroupSelected(selectedObjects: Object[]) {
  if (selectedObjects.length > 1) {
    return true;
  }

  return isObjectPartOfGroup(selectedObjects[0]);
}
